import { useState, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar1';
import Navbar from './Navbar';
import Footer from './Footer';
import ChatWidget from '../Components/Chatbox/ChatWidget';
import GuidedTour from '../Components/GuidedTour';
import { disableForEnterpriseAtom } from '../Atom/disableForEnterpriseAtom';
import { useCurrentOrder } from '../Hooks/useCurrentOrder';
import useTokenRefresh from '../Hooks/useTokenRefresh';

export default function Layout({ isVisible = true }) {
    const { t } = useTranslation();
    const disableForEnterprise = useRecoilValue(disableForEnterpriseAtom);
    const { orderData, loading: orderLoading } = useCurrentOrder();

    // Proactive token refresh while the user is on the portal
    useTokenRefresh();

    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isSidebarHovered, setIsSidebarHovered] = useState(false);

    // Crea navbarItems dinamicamente basato sui dati dell'ordine
    const navbarItems = useMemo(() => {
        // Se account non ancora visibile (ordine in attesa), blocca tutto tranne Dashboard
        const restricted = !isVisible;

        const items = [
            { label: t('sidebar.dashboard') || 'Dashboard', toLink: '/dashboard', icon: 'home', disabled: false, locked: false },
            { label: t('pluginAdmin') || 'Plugin Admin', toLink: '/pluginadmin', icon: 'listcheck', disabled: disableForEnterprise ?? false, locked: restricted },
            { label: t('compliance') || 'Compliance', toLink: '/compliance', icon: 'starshield', disabled: disableForEnterprise ?? false, locked: restricted },
            { label: t('myAsset') || 'My Assets', toLink: '/myassets', icon: 'server', disabled: disableForEnterprise ?? false, locked: restricted },
            { label: t('cyberLog') || 'Cyber Log', toLink: '/cyberlog', icon: 'newspaper', disabled: false, locked: restricted },
            { label: t('cyberSurveillance') || 'Cyber Surveillance', toLink: '/cybersurveillance', icon: 'bell', disabled: false, locked: restricted },
        ];

        // Networking Surveillance
        items.push({
            label: 'Network Surveillance',
            toLink: '/networking',
            icon: 'network',
            disabled: false,
            locked: restricted || (orderLoading ? false : !orderData?.offerta_sorveglianza_rete)
        });

        // Awareness
        items.push({
            label: 'Awareness Training',
            toLink: '/awareness',
            icon: 'certificate',
            disabled: false,
            locked: restricted || (orderLoading ? false : !orderData?.awareness_enabled)
        });

        items.push({ label: t('enterprise') || 'Enterprise', toLink: '/enterpriseConsent', icon: 'enterprise', disabled: disableForEnterprise ?? false, locked: restricted });
        return items;
    }, [t, disableForEnterprise, isVisible, orderLoading, orderData?.awareness_enabled, orderData?.offerta_sorveglianza_rete]);

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar
                navbarItems={navbarItems}
                collapsed={sidebarCollapsed}
                toggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
                isMobileOpen={isMobileSidebarOpen}
                onCloseMobile={() => setIsMobileSidebarOpen(false)}
                onHover={setIsSidebarHovered}
            />

            {/* Main content area with dynamic margin based on sidebar state */}
            <div className={`flex flex-col flex-1 transition-all duration-300 ease-in-out ${
                isSidebarHovered ? 'lg:ml-[240px]' : 'lg:ml-[70px]'
            }`}>
                <Navbar onMenuClick={() => setIsMobileSidebarOpen(true)} />
                <main className="flex-1 overflow-y-auto bg-gray-50">
                    <div className='py-6 px-6 lg:px-10'>
                        <Outlet context={{ orderData, orderLoading, isVisible }} />
                    </div>
                    <div className=''>
                        <Footer />
                    </div>
                    <ChatWidget />
                    <GuidedTour isVisible={isVisible} />
                </main>
            </div>
        </div>
    );
}
